/**
 * Menu UI state: selection, collapse, booking payload. Backed by per-date menu store.
 * Reads current selectedDate's slot; store is Master.
 */
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { DayConfig } from '../subscription/types';
import { useMenuStore } from './store';

export type DayConfigStateStub = DayConfig;

export function useMenuState() {
  const selected_date = useMenuStore(s => s.selectedDate);
  const slot = useMenuStore(s => s.getSlot(selected_date));

  const actions = useMenuStore(
    useShallow(s => ({
      setMenuData: s.setMenuData,
      setSelectedDate: s.setSelectedDate,
      setItemCustomizations: s.setItemCustomizations,
      updateItemQuantity: s.updateItemQuantity,
      updateItemScaledCarbs: s.updateItemScaledCarbs,
      prepareBookingData: s.prepareBookingData,
      clearDateDirty: s.clearDateDirty,
    })),
  );

  const menu_builders = useMemo(() => slot?.menuBuilders ?? [], [slot?.menuBuilders]);
  const day_config = slot?.dayConfig ?? null;
  const is_double_box = slot?.isDoubleBox ?? false;

  const { macroTotals, selectedMealsCount, selectedSnacksCount } = useMemo(() => {
    let calories_cents = 0;
    let protein_cents = 0;
    let carbs_cents = 0;
    let fats_cents = 0;
    let meals = 0;
    let snacks = 0;

    for (const b of menu_builders) {
      const cat = (b.type_data?.[0]?.food_type_category ?? '').toLowerCase();
      const isMeal = cat === 'meal';
      const qty = b.selected_quantity ?? 0;
      if (isMeal) {meals += qty;}
      else {snacks += qty;}

      for (const it of b.type_data ?? []) {
        const q = it.quantity ?? 0;
        if (q > 0) {
          calories_cents += Math.round(100 * parseFloat(String(it.calories ?? 0)) * q);
          protein_cents += Math.round(100 * parseFloat(String(it.protein ?? 0)) * q);
          carbs_cents += Math.round(100 * parseFloat(String(it.carbs ?? 0)) * q);
          fats_cents += Math.round(100 * parseFloat(String(it.fats ?? 0)) * q);
        }
        for (const r of (it as { replaceable_menu_items?: Array<{ quantity?: number; calories?: string | number; protein?: string | number; carbs?: string | number; fats?: string | number }> }).replaceable_menu_items ?? []) {
          const rq = r.quantity ?? 0;
          if (rq > 0) {
            calories_cents += Math.round(100 * parseFloat(String(r.calories ?? 0)) * rq);
            protein_cents += Math.round(100 * parseFloat(String(r.protein ?? 0)) * rq);
            carbs_cents += Math.round(100 * parseFloat(String(r.carbs ?? 0)) * rq);
            fats_cents += Math.round(100 * parseFloat(String(r.fats ?? 0)) * rq);
          }
        }
      }
    }
    const macroTotals = {
      calories: Math.round(calories_cents / 100),
      protein: Math.round(protein_cents / 100),
      carbs: Math.round(carbs_cents / 100),
      fats: Math.round(fats_cents / 100),
    };
    return { macroTotals, selectedMealsCount: meals, selectedSnacksCount: snacks };
  }, [menu_builders]);

  const isSelectionComplete = useCallback(() => {
    if (!day_config) {return false;}
    const need_meals = day_config.mealsCount ?? 0;
    const need_snacks = day_config.snacksCount ?? 0;
    return selectedMealsCount >= need_meals && selectedSnacksCount >= need_snacks;
  }, [day_config, selectedMealsCount, selectedSnacksCount]);

  return {
    menuBuilders: menu_builders,
    selectedDate: selected_date,
    dayConfig: day_config,
    isDoubleBox: is_double_box,
    selectedMealsCount,
    selectedSnacksCount,
    mealsCount: day_config?.mealsCount ?? 0,
    snacksCount: day_config?.snacksCount ?? 0,
    macroTotals,
    isSelectionComplete,
    isEmpty: menu_builders.length === 0,
    ...actions,
    getSlot: useMenuStore(s => s.getSlot),
    getDirtyDates: useMenuStore(s => s.getDirtyDates),
  };
}
